import { useId } from 'react';

/** Side-view oil tanker with B2B styling and blood drips (liters-saved counter). */
export function OilTankerIcon({ className }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '') || '0';
  const bodyGrad = `tanker-body-${uid}`;
  const tankGrad = `tanker-tank-${uid}`;
  const shineGrad = `tanker-shine-${uid}`;
  const bloodGrad = `tanker-blood-${uid}`;

  return (
    <svg className={className} viewBox="0 0 32 22" xmlns="http://www.w3.org/2000/svg" aria-hidden fill="none">
      <defs>
        <linearGradient id={bodyGrad} x1="0" y1="0" x2="32" y2="22" gradientUnits="userSpaceOnUse">
          <stop stopColor="#ff8aa8" />
          <stop offset="0.45" stopColor="#f94caf" />
          <stop offset="1" stopColor="#9b2d9e" />
        </linearGradient>
        <linearGradient id={tankGrad} x1="2" y1="6" x2="18" y2="16" gradientUnits="userSpaceOnUse">
          <stop stopColor="#ffb8d4" />
          <stop offset="0.35" stopColor="#f94caf" />
          <stop offset="1" stopColor="#7a2488" />
        </linearGradient>
        <linearGradient id={shineGrad} x1="4" y1="7" x2="12" y2="11" gradientUnits="userSpaceOnUse">
          <stop stopColor="#fff" stopOpacity="0.55" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={bloodGrad} x1="8" y1="14" x2="8" y2="22" gradientUnits="userSpaceOnUse">
          <stop stopColor="#ff3b4d" />
          <stop offset="0.55" stopColor="#b80e22" />
          <stop offset="1" stopColor="#5a0610" />
        </linearGradient>
        <filter id={`tanker-glow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="0.6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Ground shadow */}
      <ellipse cx="16" cy="20.2" rx="11.5" ry="1.1" fill="#000" opacity="0.35" />

      {/* Blood puddle */}
      <path
        d="M9 19.8C9 19.2 11.2 18.6 14 18.8C16.2 19 18.5 19.4 20 19.9C20.8 20.2 18.5 20.6 15 20.5C11.5 20.4 9 20.2 9 19.8Z"
        fill={`url(#${bloodGrad})`}
        opacity="0.9"
      />
      <ellipse cx="12.5" cy="19.6" rx="2.2" ry="0.55" fill="#ff5c6c" opacity="0.65" />

      {/* Rear wheel */}
      <circle cx="7.2" cy="18.4" r="2.35" fill="#1a1220" />
      <circle cx="7.2" cy="18.4" r="1.55" fill="#2d2438" />
      <circle cx="7.2" cy="18.4" r="0.75" fill="#f94caf" opacity="0.85" />

      {/* Front wheel */}
      <circle cx="22.8" cy="18.4" r="2.35" fill="#1a1220" />
      <circle cx="22.8" cy="18.4" r="1.55" fill="#2d2438" />
      <circle cx="22.8" cy="18.4" r="0.75" fill="#f94caf" opacity="0.85" />

      {/* Tank trailer */}
      <rect x="1.2" y="7.2" width="15.8" height="8.2" rx="4.1" fill={`url(#${tankGrad})`} />
      <rect x="1.2" y="7.2" width="15.8" height="8.2" rx="4.1" stroke={`url(#${bodyGrad})`} strokeWidth="0.65" />

      {/* Tank bands + rivets */}
      <rect x="5.2" y="7.5" width="0.55" height="7.6" rx="0.25" fill="#cd42d7" opacity="0.55" />
      <rect x="10.8" y="7.5" width="0.55" height="7.6" rx="0.25" fill="#cd42d7" opacity="0.55" />
      <circle cx="3.4" cy="11.3" r="0.35" fill="#ffe0f0" opacity="0.7" />
      <circle cx="14.8" cy="11.3" r="0.35" fill="#ffe0f0" opacity="0.7" />

      {/* Tank highlight */}
      <path d="M3.5 8.2H13.5C11.5 9.8 5.5 9.6 3.5 8.2Z" fill={`url(#${shineGrad})`} />

      {/* Blood drips from tank */}
      <g fill={`url(#${bloodGrad})`} filter={`url(#tanker-glow-${uid})`}>
        <path d="M6.2 15.4C6.2 16.2 5.9 17.4 5.6 18.1C5.4 18.6 5.1 18.6 5 18.1C4.7 17.2 4.5 16 4.8 15.4C5 15 6 15 6.2 15.4Z" />
        <path d="M9.8 15.5C9.9 16.4 9.6 17.8 9.3 18.5C9.1 19 8.8 19 8.7 18.4C8.5 17.3 8.4 16 8.6 15.5C8.8 15.1 9.6 15.1 9.8 15.5Z" />
        <path d="M13.2 15.3C13.3 16 13.1 17.1 12.9 17.8C12.7 18.3 12.5 18.3 12.4 17.7C12.2 16.7 12.1 15.5 12.3 15.3C12.5 14.9 13.1 14.9 13.2 15.3Z" />
      </g>
      <path d="M5.2 15.1H14.2" stroke="#ff6b7a" strokeWidth="0.45" strokeLinecap="round" opacity="0.75" />

      {/* Cab */}
      <path
        d="M17 7.4H24.2L27.2 10.2V15.4H17V7.4Z"
        fill={`url(#${bodyGrad})`}
        stroke="#ff65c8"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
      <path d="M24.2 10.2H27.2V12.2H24.2V10.2Z" fill="#ffe8f5" opacity="0.45" />
      <path d="M18.2 8.4H22.8L21.6 11.8H18.2V8.4Z" fill="#1a1028" opacity="0.55" />
      <path d="M19 9H21.8L21 11.2H19V9Z" fill="#9ee0ff" opacity="0.35" />

      {/* Exhaust / detail */}
      <rect x="26.6" y="9.8" width="1.2" height="2.8" rx="0.4" fill="#5a3d66" opacity="0.8" />
      <circle cx="27.2" cy="9.4" r="0.55" fill="#ff8aa8" opacity="0.6" />
    </svg>
  );
}
